<template>
  <div class="page-content">
    <div class="title">
      Bulk image upload
    </div>

    <div class="card upload-card">
      <div class="card-header">
        <h3>Nuevo batch</h3>
      </div>
      <p class="hint">
        Sube un archivo <strong>.zip</strong> con carpetas por código NKR (ej.
        <code>NKR12345/foto1.jpg</code>). Las imágenes se procesarán igual que
        en <code>process_new_files_multi</code>.
      </p>

      <label class="dropzone" :class="{ disabled: isBusy }">
        <input
          type="file"
          accept=".zip"
          ref="fileInput"
          @change="onFileSelected"
          :disabled="isBusy"
        />
        <span class="dropzone-icon">📦</span>
        <span v-if="selectedFile">{{ selectedFile.name }}</span>
        <span v-else>Selecciona o arrastra un archivo .zip</span>
      </label>

      <a
        class="btn-dark-button"
        v-if="selectedFile && !isBusy"
        @click="startUpload"
        >Upload zip</a
      >

      <div v-if="uploadStep" class="step-status">
        <BaseSmallSpinner />
        <div class="step-status-text">
          <span>{{ uploadStepLabel }}</span>
          <div v-if="uploadStep === 'uploading'" class="bar">
            <div class="bar-fill" :style="{ width: uploadProgress + '%' }" />
          </div>
          <span v-if="uploadStep === 'uploading'" class="bar-label"
            >{{ uploadProgress }}%</span
          >
        </div>
      </div>
    </div>

    <div class="card" v-if="currentBatch">
      <div class="card-header">
        <h3>{{ currentBatch.batchKey }}</h3>
        <span class="badge" :class="'badge-' + currentBatch.status">{{
          statusLabel(currentBatch.status)
        }}</span>
      </div>

      <template v-if="currentBatch.status === 'ready'">
        <p class="summary">
          <strong>{{ currentBatch.totalProducts }}</strong> productos ·
          <strong>{{ currentBatch.totalImages }}</strong> imágenes encontradas
          en el zip.
          <a class="toggle-link" @click="showProducts = !showProducts">{{
            showProducts ? "ocultar" : "ver productos"
          }}</a>
        </p>
        <ul class="products-list" v-if="showProducts">
          <li v-for="product in currentBatch.products" :key="product.id">
            <span class="nkr-code">{{ product.nkrCode }}</span>
            <span class="muted">{{ product.imageCount }} imágenes</span>
          </li>
        </ul>
        <a class="btn-dark-button" @click="startProcessing">Procesar</a>
      </template>

      <template
        v-else-if="
          currentBatch.status === 'processing' ||
            currentBatch.status === 'extracting'
        "
      >
        <div class="step-status">
          <BaseSmallSpinner />
          <div class="step-status-text">
            <span v-if="currentBatch.status === 'extracting'"
              >Extrayendo zip y listando productos...</span
            >
            <template v-else>
              <span
                >{{ currentBatch.processedProducts }} /
                {{ currentBatch.totalProducts }} productos</span
              >
              <div class="bar">
                <div
                  class="bar-fill"
                  :style="{ width: processingPercentage + '%' }"
                />
              </div>
              <span class="muted">
                {{ currentBatch.newImages }} nuevas,
                {{ currentBatch.alreadyExist }} ya existían
              </span>
            </template>
          </div>
        </div>
      </template>

      <template v-else-if="currentBatch.status === 'completed'">
        <p class="summary success">
          ✓ Completado: <strong>{{ currentBatch.newImages }}</strong> imágenes
          nuevas, {{ currentBatch.alreadyExist }} ya existían.
          <a class="toggle-link" @click="showProducts = !showProducts">{{
            showProducts ? "ocultar" : "ver reporte por código NKR"
          }}</a>
        </p>
      </template>

      <template v-else-if="currentBatch.status === 'error'">
        <p class="summary error">✕ {{ currentBatch.errorMessage }}</p>
      </template>

      <div
        class="table-wrapper"
        v-if="
          showProducts && currentBatch.products && currentBatch.products.length
        "
      >
        <table>
          <thead>
            <tr>
              <th>Código NKR</th>
              <th>Status</th>
              <th>Imágenes en zip</th>
              <th>Imágenes nuevas</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in currentBatch.products" :key="product.id">
              <td class="nkr-code">{{ product.nkrCode }}</td>
              <td>
                <span class="badge" :class="'badge-' + product.status">{{
                  statusLabel(product.status)
                }}</span>
              </td>
              <td>{{ product.imageCount }}</td>
              <td>{{ product.newImages }}</td>
              <td class="muted">{{ product.errorMessage }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="card history-card">
      <div class="card-header">
        <h3>Batches anteriores</h3>
      </div>
      <div class="table-wrapper" v-if="batches.length">
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Archivo</th>
              <th>Status</th>
              <th>Productos</th>
              <th>Imágenes en zip</th>
              <th>Imágenes nuevas</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="batch in batches"
              :key="batch.id"
              class="clickable-row"
              @click="viewBatch(batch.id)"
            >
              <td>{{ batch.createdAt }}</td>
              <td>{{ batch.originalFilename }}</td>
              <td>
                <span class="badge" :class="'badge-' + batch.status">{{
                  statusLabel(batch.status)
                }}</span>
              </td>
              <td>{{ batch.processedProducts }} / {{ batch.totalProducts }}</td>
              <td>{{ batch.totalImages }}</td>
              <td>{{ batch.newImages }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="muted" v-else>No hay batches anteriores.</p>
    </div>
  </div>
</template>
<script>
import {
  listImageBatches,
  getImageBatch,
  requestImageBatchUpload,
  uploadZipToS3,
  extractImageBatch,
  processNextImageBatchChunk
} from "@/api/masterAdmin/ImageBatchesService.js";

const STATUS_LABELS = {
  pending_upload: "Pendiente de subida",
  extracting: "Extrayendo",
  ready: "Listo",
  processing: "Procesando",
  completed: "Completado",
  error: "Error",
  pending: "Pendiente",
  claimed: "Procesando",
  processed: "Procesado"
};

export default {
  data() {
    return {
      selectedFile: null,
      uploadStep: null, // 'uploading' | 'extracting' | 'processing'
      uploadProgress: 0,
      currentBatch: null,
      batches: [],
      showProducts: false,
      isPolling: false
    };
  },
  computed: {
    isBusy() {
      return this.uploadStep !== null;
    },
    uploadStepLabel() {
      if (this.uploadStep === "uploading") return "Subiendo zip a S3...";
      if (this.uploadStep === "extracting")
        return "Extrayendo zip y listando productos...";
      return "";
    },
    processingPercentage() {
      if (!this.currentBatch || !this.currentBatch.totalProducts) return 0;
      return Math.min(
        100,
        Math.round(
          (this.currentBatch.processedProducts /
            this.currentBatch.totalProducts) *
            100
        )
      );
    }
  },
  mounted() {
    this.loadBatches();
  },
  beforeDestroy() {
    this.isPolling = false;
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    async loadBatches() {
      this.batches = await listImageBatches();
    },
    async viewBatch(batchId) {
      if (this.isPolling) return;
      this.currentBatch = await getImageBatch(batchId);
      this.showProducts = true;
    },
    onFileSelected(event) {
      this.selectedFile = event.target.files[0] || null;
    },
    async startUpload() {
      if (!this.selectedFile) return;
      try {
        this.uploadStep = "uploading";
        this.uploadProgress = 0;
        const { batch, uploadUrl } = await requestImageBatchUpload(
          this.selectedFile.name
        );
        this.currentBatch = batch;

        await uploadZipToS3(uploadUrl, this.selectedFile, progressEvent => {
          if (progressEvent.total) {
            this.uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
          }
        });

        this.uploadStep = "extracting";
        await extractImageBatch(batch.id);
        this.currentBatch = await getImageBatch(batch.id);
        this.showProducts = false;

        this.selectedFile = null;
        this.$refs.fileInput.value = "";
        this.uploadStep = null;
        this.loadBatches();
      } catch (e) {
        this.uploadStep = null;
        // eslint-disable-next-line no-console
        console.error(e);
        alert("Error subiendo/extrayendo el batch: " + (e?.message || e));
      }
    },
    async startProcessing() {
      if (!this.currentBatch || this.isPolling) return;
      this.uploadStep = "processing";
      this.isPolling = true;
      const batchId = this.currentBatch.id;

      // sequential polling: wait for each chunk to finish before requesting the next one,
      // so slow requests never overlap and reprocess the same products
      while (this.isPolling) {
        try {
          const { batch, done } = await processNextImageBatchChunk(batchId, 3);
          this.currentBatch = batch;
          if (done) {
            this.isPolling = false;
            this.uploadStep = null;
            this.currentBatch = await getImageBatch(batchId);
            this.loadBatches();
          }
        } catch (e) {
          this.isPolling = false;
          this.uploadStep = null;
          // eslint-disable-next-line no-console
          console.error(e);
          alert("Error procesando el batch: " + (e?.message || e));
        }
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/App.scss";

.page-content {
  max-width: 960px;
  margin: 0 auto;
}

.card {
  margin: 0 0 20px;
  padding: 24px;
  border-radius: 10px;
  background: $white;
  @extend .shadow-4dp;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $mainTextColor;
  }
}

.hint {
  color: $mainTextColor;
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 16px;
  code {
    background: $M3;
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 12px;
  }
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 28px 16px;
  margin-bottom: 16px;
  border: 2px dashed $M7;
  border-radius: 8px;
  color: $mainTextColor;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: $link-color-1;
    background-color: $M3;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  input[type="file"] {
    display: none;
  }
}

.dropzone-icon {
  font-size: 28px;
}

.step-status {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}

.step-status-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: $mainTextColor;
  flex: 1;
}

.bar {
  width: 100%;
  max-width: 320px;
  height: 8px;
  border-radius: 4px;
  background: $M3;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: $link-color-1;
  transition: width 0.2s ease;
}

.bar-label {
  font-size: 12px;
  color: $M7;
}

.summary {
  font-size: 15px;
  color: $mainTextColor;
  margin: 0 0 14px;

  &.success {
    color: #1a7f4e;
  }
  &.error {
    color: $nkr-red;
  }
}

.toggle-link {
  margin-left: 8px;
  color: $link-color-1;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.products-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid $M8;
  border-radius: 8px;

  li {
    display: flex;
    justify-content: space-between;
    padding: 8px 14px;
    border-bottom: 1px solid $M8;
    font-size: 13px;
    &:last-child {
      border-bottom: none;
    }
  }
}

.nkr-code {
  font-weight: 600;
  color: $M10;
}

.muted {
  color: $M7;
  font-size: 12px;
}

.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background: $M3;
  color: $mainTextColor;

  &.badge-completed,
  &.badge-processed {
    background: #e2f6ec;
    color: #1a7f4e;
  }
  &.badge-error {
    background: #fde8e8;
    color: $nkr-red;
  }
  &.badge-processing,
  &.badge-claimed,
  &.badge-extracting {
    background: #fff3d9;
    color: #a9720b;
  }
  &.badge-ready {
    background: #e5f0ff;
    color: #1d5fb0;
  }
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid $M8;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 14px;
    background: $tableHeaderBG;
    color: $tableHeaderText;
    font-weight: 600;
    white-space: nowrap;
  }

  td {
    text-align: left;
    padding: 10px 14px;
    border-top: 1px solid $M8;
  }

  .clickable-row {
    cursor: pointer;
    transition: background-color 0.1s;
    &:hover {
      background-color: $M3;
    }
  }
}
</style>
